import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { pluginRegistry, FrontendPlugin, SidebarMenuItem, AIAction } from './plugin-loader';

interface PluginContextValue {
  plugins: FrontendPlugin[];
  sidebarMenus: SidebarMenuItem[];
  aiActions: AIAction[];
  isLoading: boolean;
  getPlugin: (name: string) => FrontendPlugin | undefined;
  executeAIAction: (actionId: string, params: Record<string, unknown>) => Promise<unknown>;
}

const PluginContext = createContext<PluginContextValue | null>(null);

interface PluginProviderProps {
  children: ReactNode;
}

export function PluginProvider({ children }: PluginProviderProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [plugins, setPlugins] = useState<FrontendPlugin[]>([]);
  const [sidebarMenus, setSidebarMenus] = useState<SidebarMenuItem[]>([]);
  const [aiActions, setAiActions] = useState<AIAction[]>([]);

  useEffect(() => {
    async function loadPlugins() {
      try {
        // 动态导入生成的插件注册代码
        const { registerPlugins } = await import('@generated/plugin-registry');
        
        // 注册所有插件
        registerPlugins(pluginRegistry);
        
        // 初始化插件
        await pluginRegistry.initAll();
        
        // 更新状态
        setPlugins(pluginRegistry.getAllPlugins());
        setSidebarMenus(pluginRegistry.getSidebarMenus());
        setAiActions(pluginRegistry.getAIActions());
      } catch (error) {
        console.error('Failed to load plugins:', error);
        // 即使没有插件也要继续
      } finally {
        setIsLoading(false);
      }
    }

    loadPlugins();
  }, []);

  const getPlugin = (name: string) => {
    return pluginRegistry.getPlugin(name);
  };

  const executeAIAction = async (actionId: string, params: Record<string, unknown>) => {
    const action = pluginRegistry.getAIAction(actionId);
    if (!action) {
      throw new Error(`AI action "${actionId}" not found`);
    }
    
    const context = {
      currentRoute: window.location.pathname,
      user: null, // TODO: 从 store 获取
    };
    
    return action.handler(params, context);
  };

  const value: PluginContextValue = {
    plugins,
    sidebarMenus,
    aiActions,
    isLoading,
    getPlugin,
    executeAIAction,
  };

  return (
    <PluginContext.Provider value={value}>
      {children}
    </PluginContext.Provider>
  );
}

export function usePlugins() {
  const context = useContext(PluginContext);
  if (!context) {
    throw new Error('usePlugins must be used within a PluginProvider');
  }
  return context;
}

